import { ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';
import 'swiper/css';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';

import { DefaultButtonStyled } from '@/components/atom/buttons/DefaultButton';
import StickyButton from '@/components/atom/buttons/StickyButton';
import Dot from '@/components/atom/icon/Dot';
import BottomSheet from '@/components/molecules/BottomSheet';
import useNeverSeeAgain from '@/hooks/useNeverSeeAgain';
import theme from '@/styles/theme';

export interface OnBoardingSteps {
  subTitle: string;
  title: ReactNode;
  guideImage: string;
}

const onBoardingSteps: Array<OnBoardingSteps> = [
  {
    subTitle: '실시간 의료상담, 처음이시죠?',
    title: '쉽게 이용할 수 있도록\n설명해드릴게요!',
    guideImage: '/images/writeOnboarding/writeOnboarding_1_v3.png',
  },
  {
    subTitle: '사소한 질문도 괜찮아요',
    title: '실시간 상담은\n어떤 고민에도 귀기울여요',
    guideImage: '/images/writeOnboarding/writeOnboarding_2_v3.png',
  },
  {
    subTitle: '설명이 어렵다면 사진으로',
    title: '사진을 첨부하면 \n더 자세히 상담해드릴 수 있어요',
    guideImage: '/images/writeOnboarding/writeOnboarding_3_v3.png',
  },
  {
    subTitle: '안심하세요!',
    title: '의료진의 판단 하에\n개인정보 노출을 보호해드려요',
    guideImage: '/images/writeOnboarding/writeOnboarding_4_v3.png',
  },
];

interface WriteOnBoardingProps {
  isVisible: boolean;
  onCloseGuide: (skip?: boolean) => void;
}
const WriteOnBoarding = ({ isVisible, onCloseGuide }: WriteOnBoardingProps) => {
  useNeverSeeAgain();
  const [currentStep, setCurrentStep] = useState<number>(0);

  const onChangeStep = (step: number) => {
    setCurrentStep(step);
  };

  const swiperRef = useRef<SwiperRef>(null);
  const slideNext = (changeStep: number) => {
    onChangeStep(changeStep);
    swiperRef.current?.swiper.slideNext();
  };

  if (typeof window !== 'object') return <></>;
  return (
    <BottomSheet isOpened={isVisible} index={10}>
      <Wrapper>
        <Header>
          {currentStep !== onBoardingSteps.length - 1 && (
            <Skip onClick={() => onCloseGuide(true)}>건너뛰기</Skip>
          )}
        </Header>
        <Body>
          <SwiperWrapper
            slidesPerView={1}
            spaceBetween={20}
            onSlideChange={(e) => onChangeStep(e.activeIndex)}
            ref={swiperRef}
          >
            {onBoardingSteps.map((step, key) => (
              <SwiperSlide key={key}>
                <SubTitle>{step.subTitle}</SubTitle>
                <Title>{step.title}</Title>
                <GuideImage src={step.guideImage} />
              </SwiperSlide>
            ))}
          </SwiperWrapper>
        </Body>
        <Bottom>
          <Pagination>
            {onBoardingSteps.map((_, key) => (
              <Dot
                key={key}
                size={8}
                color={
                  currentStep === key
                    ? theme.color.primary500
                    : theme.color.gray100
                }
              />
            ))}
          </Pagination>
          {currentStep !== onBoardingSteps.length - 1 ? (
            <StickyButton>
              <StickyButton.Button
                text={'다음'}
                onClick={() => slideNext(currentStep + 1)}
              />
            </StickyButton>
          ) : (
            <StickyButton>
              <StickyButton.Button
                text={'이제 질문을 남겨보세요'}
                onClick={() => onCloseGuide()}
              />
            </StickyButton>
          )}
        </Bottom>
      </Wrapper>
    </BottomSheet>
  );
};

export default WriteOnBoarding;

const Wrapper = styled.div`
  padding-top: 17px;
  text-align: center;
  max-height: 95vh;
  overflow-y: scroll;

  * {
    .swiper-pagination-horizontal {
      position: initial;
      margin-top: 12px;
      display: flex;
      justify-content: center;
    }
  }
`;
const Header = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  align-items: center;
  margin: 0 20px;
`;

const Pagination = styled.div`
  padding-top: 30px;
  * {
    &:not(:last-child) {
      margin-right: 8px;
    }
  }
`;

const Skip = styled(DefaultButtonStyled)`
  position: absolute;
  right: 0;
  ${({ theme }) => `
    ${theme.typography.UI15R};
    color: ${theme.color.gray300}
  `};
`;

const Body = styled.div`
  margin: 40px 0 2px;

  * {
    .swiper {
      padding: 0 20px;
    }
  }
`;

const SwiperWrapper = styled(Swiper)`
  padding: 0 20px;
`;

const Title = styled.div`
  ${({ theme }) => theme.typography.UI20SB};
  margin-bottom: 6px;
  white-space: break-spaces;
`;

const SubTitle = styled.div`
  padding-bottom: 12px;
  ${({ theme }) => `
    ${theme.typography.UI17SB};
    color: ${theme.color.primary700}
  `};
`;

const GuideImage = styled.img`
  margin-top: 37px;
  max-width: 320px;
  width: 100%;
`;

const Bottom = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;
